/**
 * Página de Historial de Supervisiones
 * Muestra el registro de supervisiones pasadas con intervenciones para auditoría y debriefing
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supervisionV2API } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  History,
  Clock,
  User,
  Eye,
  Hand,
  Lightbulb,
  MessageSquare,
  Zap,
  Activity,
  FileText,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Calendar,
  BarChart3,
  TrendingUp,
  Users,
  Target,
  AlertTriangle,
  Pause,
  Play,
  ArrowRight,
  Download,
  RefreshCw
} from 'lucide-react';

// Formato de fecha
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

// Formato de duración
const formatDuration = (minutes) => {
  if (!minutes) return '-';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Color de modo
const getModeColor = (mode) => {
  switch (mode) {
    case 'observador': return 'bg-blue-100 text-blue-800';
    case 'asistido': return 'bg-yellow-100 text-yellow-800';
    case 'control_total': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

// Ícono de modo
const getModeIcon = (mode) => {
  switch (mode) {
    case 'observador': return <Eye className="w-3 h-3" />;
    case 'asistido': return <Lightbulb className="w-3 h-3" />;
    case 'control_total': return <Hand className="w-3 h-3" />;
    default: return null;
  }
};

// Tarjeta de estadística
const StatCard = ({ title, value, icon: Icon, color = "blue", trend = null }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {trend && (
            <p className={`text-xs mt-1 ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend > 0 ? '+' : ''}{trend}% vs anterior
            </p>
          )}
        </div>
        <div className={`w-10 h-10 rounded-full bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Componente de detalle de supervisión
const SupervisionDetailModal = ({ supervisionId, isOpen, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && supervisionId) {
      loadDetail();
    }
  }, [isOpen, supervisionId]);
  
  const loadDetail = async () => {
    setLoading(true);
    try {
      const response = await supervisionV2API.getSupervisionDetail(supervisionId);
      setDetail(response.data);
    } catch (error) {
      toast.error('Error al cargar detalle');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Detalle de Supervisión
          </DialogTitle>
          <DialogDescription>
            Registro completo de la sesión de supervisión para auditoría y debriefing
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : detail ? (
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-6">
              {/* Info general */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Duración</p>
                  <p className="font-bold">{formatDuration(detail.supervision?.duration_minutes)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Modo</p>
                  <Badge className={getModeColor(detail.supervision?.mode)}>
                    {detail.supervision?.mode}
                  </Badge>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Intervenciones</p>
                  <p className="font-bold">{detail.interventions_count}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Mensajes</p>
                  <p className="font-bold">{detail.messages_count}</p>
                </div>
              </div>
              
              {/* Estudiante y escenario */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Estudiante
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    {detail.student ? (
                      <>
                        <p className="font-medium">{detail.student.nombre} {detail.student.apellido}</p>
                        <p className="text-gray-500">{detail.student.email}</p>
                      </>
                    ) : (
                      <p className="text-gray-400">No disponible</p>
                    )}
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Escenario
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm">
                    {detail.scenario ? (
                      <>
                        <p className="font-medium">{detail.scenario.nombre}</p>
                        <p className="text-gray-500">{detail.scenario.especialidad}</p>
                      </>
                    ) : (
                      <p className="text-gray-400">No disponible</p>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* Resumen de actividad */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Resumen de Actividad
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
                    <div className="bg-orange-50 rounded-lg p-2">
                      <Zap className="w-4 h-4 mx-auto text-orange-500 mb-1" />
                      <p className="text-lg font-bold">{detail.activity_summary?.clinical_events_activated || 0}</p>
                      <p className="text-xs text-gray-500">Eventos Clínicos</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2">
                      <Activity className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                      <p className="text-lg font-bold">{detail.activity_summary?.vitals_modifications || 0}</p>
                      <p className="text-xs text-gray-500">Cambios Vitales</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-2">
                      <Pause className="w-4 h-4 mx-auto text-purple-500 mb-1" />
                      <p className="text-lg font-bold">{detail.activity_summary?.pauses || 0}</p>
                      <p className="text-xs text-gray-500">Pausas</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                      <MessageSquare className="w-4 h-4 mx-auto text-green-500 mb-1" />
                      <p className="text-lg font-bold">{detail.activity_summary?.messages_sent || 0}</p>
                      <p className="text-xs text-gray-500">Mensajes</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-2">
                      <Lightbulb className="w-4 h-4 mx-auto text-yellow-500 mb-1" />
                      <p className="text-lg font-bold">{detail.activity_summary?.hints_sent || 0}</p>
                      <p className="text-xs text-gray-500">Pistas</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Timeline de intervenciones */}
              {detail.interventions?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Cronología de Intervenciones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[300px] overflow-auto">
                      {detail.interventions.map((intervention, idx) => (
                        <div 
                          key={idx}
                          className="flex items-start gap-3 p-2 bg-gray-50 rounded-lg text-sm"
                        >
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            {intervention.type === 'vitals_modification' && <Activity className="w-4 h-4 text-purple-600" />}
                            {intervention.type === 'clinical_event_activation' && <Zap className="w-4 h-4 text-orange-600" />}
                            {intervention.type === 'pause' && <Pause className="w-4 h-4 text-gray-600" />}
                            {intervention.type === 'resume' && <Play className="w-4 h-4 text-green-600" />}
                            {!['vitals_modification', 'clinical_event_activation', 'pause', 'resume'].includes(intervention.type) && 
                              <Hand className="w-4 h-4 text-blue-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium capitalize">
                                {intervention.type?.replace(/_/g, ' ')}
                              </span>
                              {intervention.elapsed_seconds != null && (
                                <Badge variant="outline" className="text-xs">
                                  {Math.floor(intervention.elapsed_seconds / 60)}:{String(intervention.elapsed_seconds % 60).padStart(2, '0')}
                                </Badge>
                              )}
                            </div>
                            {intervention.details && Object.keys(intervention.details).length > 0 && (
                              <p className="text-xs text-gray-500 mt-1 truncate">
                                {JSON.stringify(intervention.details).slice(0, 100)}...
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {formatDate(intervention.timestamp)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Notas del instructor */}
              {detail.notes?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Notas del Instructor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {detail.notes.map((note, idx) => (
                        <div key={idx} className="p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                          <p className="text-sm">{note.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                            <Badge variant="outline">{note.note_type}</Badge>
                            <span>{formatDate(note.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </ScrollArea>
        ) : (
          <p className="text-center text-gray-500 py-8">No se encontró información</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Componente principal
export default function SupervisionHistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSupervision, setSelectedSupervision] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // Filtros
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statsPeriod, setStatsPeriod] = useState('month');
  
  // Cargar datos
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await supervisionV2API.getHistory({
        page,
        page_size: pageSize
      });
      setHistory(response.data.supervisions || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);
  
  const loadStats = useCallback(async () => {
    try {
      const response = await supervisionV2API.getStats(statsPeriod);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [statsPeriod]);
  
  const loadLimits = useCallback(async () => {
    try {
      const response = await supervisionV2API.getLimits();
      setLimits(response.data);
    } catch (error) {
      console.error('Error loading limits:', error);
    }
  }, []);
  
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);
  
  useEffect(() => {
    loadStats();
  }, [loadStats]);
  
  useEffect(() => {
    loadLimits();
  }, [loadLimits]);
  
  // Abrir detalle
  const handleViewDetail = (supervision) => {
    setSelectedSupervision(supervision.id);
    setShowDetail(true);
  };
  
  // Filtrar historial
  const filteredHistory = history.filter(sup => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      sup.student?.nombre?.toLowerCase().includes(query) ||
      sup.student?.apellido?.toLowerCase().includes(query) ||
      sup.scenario?.nombre?.toLowerCase().includes(query)
    );
  });
  
  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="w-6 h-6 text-purple-600" />
            Historial de Supervisiones
          </h1>
          <p className="text-sm text-gray-600">
            Registro de supervisiones para auditoría y debriefing
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/app/instructor/supervision')}>
            <Eye className="w-4 h-4 mr-2" />
            Supervisión en Vivo
          </Button>
          <Button variant="outline" onClick={() => { loadHistory(); loadStats(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Límites actuales */}
      {limits && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Límite de Supervisiones Simultáneas</p>
                  <p className="text-sm text-gray-500">{limits.message}</p>
                </div>
              </div>
              {!limits.is_unlimited && (
                <div className="text-right">
                  <p className="text-2xl font-bold">
                    {limits.current_count}/{limits.max_simultaneous}
                  </p>
                  <div className="w-32 h-2 bg-gray-200 rounded-full mt-1">
                    <div 
                      className={`h-2 rounded-full ${
                        limits.percentage_used > 80 ? 'bg-red-500' : 
                        limits.percentage_used > 50 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(limits.percentage_used, 100)}%` }}
                    />
                  </div>
                </div>
              )}
              {limits.is_unlimited && (
                <Badge className="bg-green-500 text-white">Ilimitado</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Estadísticas */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Estadísticas
          </h2>
          <Select value={statsPeriod} onValueChange={setStatsPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Última semana</SelectItem>
              <SelectItem value="month">Último mes</SelectItem>
              <SelectItem value="year">Último año</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard 
              title="Total Supervisiones" 
              value={stats.total_supervisions} 
              icon={Eye}
              color="blue"
            />
            <StatCard 
              title="Activas Ahora" 
              value={stats.active_supervisions} 
              icon={Activity}
              color="green"
            />
            <StatCard 
              title="Estudiantes Únicos" 
              value={stats.unique_students_supervised} 
              icon={Users}
              color="purple"
            />
            <StatCard 
              title="Intervenciones" 
              value={stats.total_interventions} 
              icon={Zap}
              color="orange"
            />
            <StatCard 
              title="Duración Promedio" 
              value={`${stats.average_duration_minutes} min`} 
              icon={Clock}
              color="cyan"
            />
            <StatCard 
              title="Con Control Total" 
              value={stats.by_mode?.control_total || 0} 
              icon={Hand}
              color="red"
            />
          </div>
        )}
      </div>
      
      {/* Tabla de historial */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Registro de Supervisiones</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-[200px]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No hay supervisiones registradas</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Estudiante</TableHead>
                    <TableHead>Escenario</TableHead>
                    <TableHead>Modo</TableHead>
                    <TableHead className="text-center">Duración</TableHead>
                    <TableHead className="text-center">Intervenciones</TableHead>
                    <TableHead className="text-center">Mensajes</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHistory.map((sup) => (
                    <TableRow key={sup.id} className="cursor-pointer hover:bg-gray-50">
                      <TableCell className="text-sm">
                        {formatDate(sup.started_at)}
                      </TableCell>
                      <TableCell>
                        {sup.student ? (
                          <div>
                            <p className="font-medium text-sm">{sup.student.nombre} {sup.student.apellido}</p>
                            <p className="text-xs text-gray-500">{sup.student.email}</p>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {sup.scenario?.nombre || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getModeColor(sup.mode)} text-xs`}>
                          <span className="mr-1">{getModeIcon(sup.mode)}</span>
                          {sup.mode}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {formatDuration(sup.duration_minutes)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{sup.interventions_count}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{sup.messages_count}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewDetail(sup)}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Paginación */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-gray-500">
                  Página {page} de {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Modal de detalle */}
      <SupervisionDetailModal
        supervisionId={selectedSupervision}
        isOpen={showDetail}
        onClose={() => {
          setShowDetail(false);
          setSelectedSupervision(null);
        }}
      />
    </div>
  );
}
